import React, { useMemo } from 'react';
import { Box, Typography, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter } from '@mui/material';
import autoTable from 'jspdf-autotable';
import { formatCurrency, triggerAndSetCalculations, formatDate } from '../../../utils/financialCalculations';
import { formatPercent, formatPeriodDisplay } from '../../../utils/formatting';

const RenderLossTable = ({ title, data, reportData }) => {
    const isFutureLossTable = title.toLowerCase().includes('future');

    const tableColumns = [
        { id: 'periodDisplay', label: 'Period', minWidth: 170 },
        { id: 'sourceDescription', label: 'Source', minWidth: 150 },
        { id: 'originalAmount', label: 'Amount', format: formatCurrency, align: 'right', minWidth: 100 },
        { id: 'payInterval', label: 'Pay Interval', minWidth: 100, format: (val) => val ? val.replace(/([A-Z])/g, ' $1').trim() : 'N/A' },
        { id: 'grossIncome', label: 'Gross Income', format: formatCurrency, align: 'right', minWidth: 120 },
        { id: 'lostCapacity', label: 'Lost Capacity', format: (val) => formatPercent(val, 0), align: 'right', minWidth: 100 },
        { id: 'netLoss', label: 'Net Loss', format: formatCurrency, align: 'right', minWidth: 120 },
    ];

    if (isFutureLossTable) {
        tableColumns.push(
            { id: 'pvDiscount', label: 'PV Discount', format: formatCurrency, align: 'right', minWidth: 120 },
            { id: 'presentValue', label: 'Present Value', format: formatCurrency, align: 'right', minWidth: 120 }
        );
    }

    if (!data || data.length === 0) {
        return <Typography sx={{ mt: 1, fontStyle: 'italic' }}>No {title.toLowerCase()} data available.</Typography>;
    }

    const grandTotalGross = data.reduce((acc, yearItem) => acc + (yearItem.annualGrossIncome || 0), 0);
    const grandTotalNet = data.reduce((acc, yearItem) => acc + (yearItem.annualNetLoss || 0), 0);
    let grandTotalPV = 0;
    let grandTotalPVDiscount = 0;
    if (isFutureLossTable) {
        grandTotalPV = data.reduce((acc, yearItem) => acc + (yearItem.annualPresentValue || 0), 0);
        grandTotalPVDiscount = data.reduce((acc, yearItem) => acc + (yearItem.annualPresentValueDiscountAmount || 0), 0);
    }

    return (
        <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>{title}</Typography>
            <TableContainer component={Paper} elevation={1} sx={{ maxHeight: 800 }}>
                <Table stickyHeader size="small" aria-label={`${title} table`}>
                    <TableHead>
                        <TableRow>
                            {tableColumns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.align || 'left'}
                                    style={{ minWidth: column.minWidth, fontWeight: 'bold', backgroundColor: '#eee' }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((yearItem) => (
                            <React.Fragment key={`year-${yearItem.year}`}>
                                {yearItem.periods.map((period, periodIndex) => (
                                    <TableRow key={`period-${yearItem.year}-${period.originalSourceId || periodIndex}`}>
                                        <TableCell>{formatPeriodDisplay(period.firstStartDateInYear, period.lastEndDateInYear)}</TableCell>
                                        <TableCell>{period.description}</TableCell>
                                        <TableCell align="right">{formatCurrency(period.originalAmount)}</TableCell>
                                        <TableCell>{period.payInterval ? period.payInterval.replace(/([A-Z])/g, ' $1').trim() : 'N/A'}</TableCell>
                                        <TableCell align="right">{formatCurrency(period.totalGrossIncomeInYear)}</TableCell>
                                        <TableCell align="right">{formatPercent(period.lostCapacity, 0)}</TableCell>
                                        <TableCell align="right">{formatCurrency(period.totalNetLossInYear)}</TableCell>
                                        {isFutureLossTable && (
                                            <>
                                                <TableCell align="right">{formatCurrency(period.presentValueDiscountAmount)}</TableCell>
                                                <TableCell align="right">{formatCurrency(period.presentValue)}</TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                ))}
                                <TableRow sx={{ '& > td': { fontWeight: 'bold', backgroundColor: '#f0f0f0', borderTop: '2px solid #ccc' } }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{`${yearItem.year} Total`}</TableCell>
                                    <TableCell>—</TableCell>
                                    <TableCell>—</TableCell>
                                    <TableCell>—</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(yearItem.annualGrossIncome)}</TableCell>
                                    <TableCell>—</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(yearItem.annualNetLoss)}</TableCell>
                                    {isFutureLossTable && (
                                        <>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(yearItem.annualPresentValueDiscountAmount)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(yearItem.annualPresentValue)}</TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </React.Fragment>
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow sx={{ '& > *': {
                            fontWeight: 'bold',
                            backgroundColor: '#ccc',
                            borderTop: '3px solid #999',
                            fontSize: '0.9rem',
                            padding: '8px 16px',
                            color: '#000',
                        } }}>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem', padding: '8px 16px', color: '#000' }}>Total</TableCell>
                            <TableCell sx={{ fontSize: '0.9rem', padding: '8px 16px', color: '#000' }}>—</TableCell>
                            <TableCell sx={{ fontSize: '0.9rem', padding: '8px 16px', color: '#000' }}>—</TableCell>
                            <TableCell sx={{ fontSize: '0.9rem', padding: '8px 16px', color: '#000' }}>—</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.9rem', padding: '8px 16px', color: '#000' }}>{formatCurrency(grandTotalGross)}</TableCell>
                            <TableCell sx={{ fontSize: '0.9rem', padding: '8px 16px', color: '#000' }}>—</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.9rem', padding: '8px 16px', color: '#000' }}>{formatCurrency(grandTotalNet)}</TableCell>
                            {isFutureLossTable && (
                                <>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.9rem', padding: '8px 16px', color: '#000' }}>{formatCurrency(grandTotalPVDiscount)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.9rem', padding: '8px 16px', color: '#000' }}>{formatCurrency(grandTotalPV)}</TableCell>
                                </>
                            )}
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    );
};

const EconomicDamagesSummary = ({ reportData }) => {
    const { incomeSummary, detailedPastLosses, detailedFutureLosses } = useMemo(() => {
        if (!reportData || Object.keys(reportData).length === 0) {
            return {
                incomeSummary: { totalPastLoss: 0, totalFutureLoss: 0, grandTotalLoss: 0, preJudgementInterest: 0, presentValueDiscount: 0, error: null },
                detailedPastLosses: [],
                detailedFutureLosses: [],
            };
        }
        const results = triggerAndSetCalculations(reportData);
        return {
            incomeSummary: results.summary || { totalPastLoss: 0, totalFutureLoss: 0, grandTotalLoss: 0, preJudgementInterest: 0, presentValueDiscount: 0, error: null },
            detailedPastLosses: results.detailedPastLosses || [],
            detailedFutureLosses: results.detailedFutureLosses || [],
        };
    }, [reportData]);

    const claimant = {
        name: reportData.claimantName || reportData.fullName || 'N/A',
    };

    return (
        <>
            <Box className="report-section" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Loss of Income Summary</Typography>
                <Typography paragraph>
                    This section outlines the calculated past and future loss of income for {claimant.name}, based on the provided income sources and relevant dates.
                </Typography>
                <Typography sx={{ mb: 0.5 }}><strong>Total Past Loss (Present Value):</strong> {formatCurrency(incomeSummary.totalPastLoss)}</Typography>
                <Typography sx={{ mb: 0.5 }}><strong>Total Future Loss (Nominal Value):</strong> {formatCurrency(incomeSummary.totalFutureLoss)}</Typography>
                <Typography sx={{ mb: 0.5 }}><strong>Total Future Loss (Present Value):</strong> {formatCurrency(incomeSummary.totalFuturePresentValue)}</Typography>
                <Typography sx={{ mb: 0.5, fontWeight: 'bold' }}><strong>Grand Total Net Loss (Past Loss + PV of Future Loss):</strong> {formatCurrency(incomeSummary.grandTotalLoss)}</Typography>
                {incomeSummary.error && <Typography color="error" sx={{ mt: 1 }}>Error in summary: {incomeSummary.error}</Typography>}
                {detailedPastLosses.length > 0 && (
                    <RenderLossTable
                        title="Past Losses by Year"
                        data={detailedPastLosses}
                        reportData={reportData}
                    />
                )}
                {detailedFutureLosses.length > 0 && (
                    <RenderLossTable
                        title="Future Losses by Year"
                        data={detailedFutureLosses}
                        reportData={reportData}
                    />
                )}
                {(detailedPastLosses.length > 0 || detailedFutureLosses.length > 0) && (
                    <Box sx={{ mt: 2, mb: 2, p: 2, backgroundColor: '#ccc', borderTop: '3px solid #999', borderRadius: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#000' }}>
                            Grand Total (Past Loss + PV of Future Loss): {formatCurrency(incomeSummary.grandTotalLoss)}
                        </Typography>
                    </Box>
                )}
                {(!detailedPastLosses.length && !detailedFutureLosses.length) && !incomeSummary.error && (
                     <Typography sx={{mt: 2}}>
                        No detailed income loss periods were calculated. This may be because the income sources entered do not overlap with the incident and valuation dates, or the loss duration is zero. Please review the input dates and income source periods.
                     </Typography>
                )}
            </Box>
            <Divider sx={{ my: 2 }} />
        </>
    );
};

EconomicDamagesSummary.addPdfContent = (pdf, reportData, y) => {
    const { summary: incomeSummary, detailedPastLosses, detailedFutureLosses } = triggerAndSetCalculations(reportData);
    const claimant = {
        name: reportData.claimantName || reportData.fullName || 'N/A',
    };

    pdf.setFont('helvetica', 'bold');
    pdf.text('Loss of Income Summary', 15, y);
    y += 7;
    pdf.setFont('helvetica', 'normal');
    const lossSummaryParagraph = `This section outlines the calculated past and future loss of income for ${claimant.name}, based on the provided income sources and relevant dates.`;
    const wrappedLossSummary = pdf.splitTextToSize(lossSummaryParagraph, 180);
    pdf.text(wrappedLossSummary, 15, y);
    y += 6 * wrappedLossSummary.length + 2;
    const lossSummaryKeyLines = [
        `Total Past Loss (Present Value): ${formatCurrency(incomeSummary.totalPastLoss)}`,
        `Total Future Loss (Nominal Value): ${formatCurrency(incomeSummary.totalFutureLoss)}`,
        `Total Future Loss (Present Value): ${formatCurrency(incomeSummary.totalFuturePresentValue)}`,
        `Grand Total Net Loss (Past Loss + PV of Future Loss): ${formatCurrency(incomeSummary.grandTotalLoss)}`,
    ];
    lossSummaryKeyLines.forEach(line => {
        const wrapped = pdf.splitTextToSize(line, 180);
        pdf.text(wrapped, 15, y);
        y += 6 * wrapped.length;
    });

    if (detailedPastLosses.length > 0) {
        y += 4;
        pdf.setFont('helvetica', 'bold');
        pdf.text('Past Losses by Year', 15, y);
        y += 4;
        pdf.setFont('helvetica', 'normal');
        autoTable(pdf, {
            startY: y,
            head: [[
                'Period', 'Source', 'Amount', 'Pay Interval', 'Gross Income', 'Lost Capacity', 'Net Loss'
            ]],
            body: detailedPastLosses.flatMap(yearItem => {
                const rows = [];
                yearItem.periods.forEach(period => {
                    let periodLabel = '';
                    if (period.firstStartDateInYear && period.lastEndDateInYear) {
                        periodLabel = `${period.firstStartDateInYear} - ${period.lastEndDateInYear}`;
                    } else if (yearItem.year) {
                        periodLabel = `${yearItem.year}`;
                    }
                    rows.push([
                        periodLabel,
                        period.description,
                        formatCurrency(period.originalAmount),
                        period.payInterval ? period.payInterval.replace(/([A-Z])/g, ' $1').trim() : 'N/A',
                        formatCurrency(period.totalGrossIncomeInYear),
                        formatPercent(period.lostCapacity, 0),
                        formatCurrency(period.totalNetLossInYear),
                    ]);
                });
                rows.push([
                    `${yearItem.year} Total`, '', '', '',
                    formatCurrency(yearItem.annualGrossIncome),
                    '',
                    formatCurrency(yearItem.annualNetLoss)
                ]);
                return rows;
            }),
            theme: 'grid',
            styles: { fontSize: 9 },
            columnStyles: {
                2: { halign: 'right' },
                4: { halign: 'right' },
                6: { halign: 'right' },
            },
            didParseCell: function (data) {
                if (data.row.raw && typeof data.row.raw[0] === 'string' && data.row.raw[0].endsWith('Total')) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                }
            }
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    if (detailedFutureLosses.length > 0) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Future Losses by Year', 15, y);
        y += 4;
        pdf.setFont('helvetica', 'normal');
        autoTable(pdf, {
            startY: y,
            head: [[
                'Year', 'Source', 'Amount', 'Pay Interval', 'Gross Income', 'Lost Capacity', 'Net Loss', 'PV Discount', 'Present Value'
            ]],
            body: (() => {
                const rows = [];
                let grandTotalGross = 0;
                let grandTotalNet = 0;
                let grandTotalPVDiscount = 0;
                let grandTotalPV = 0;
                detailedFutureLosses.forEach(yearItem => {
                    let yearGross = 0, yearNet = 0;
                    yearItem.periods.forEach(period => {
                        yearGross += period.totalGrossIncomeInYear || 0;
                        yearNet += period.totalNetLossInYear || 0;
                        rows.push([
                            period.firstStartDateInYear && period.lastEndDateInYear
                                ? `${period.firstStartDateInYear} - ${period.lastEndDateInYear}`
                                : `${yearItem.year}`,
                            period.description,
                            formatCurrency(period.originalAmount),
                            period.payInterval ? period.payInterval.replace(/([A-Z])/g, ' $1').trim() : 'N/A',
                            formatCurrency(period.totalGrossIncomeInYear),
                            formatPercent(period.lostCapacity, 0),
                            formatCurrency(period.totalNetLossInYear),
                            formatCurrency(period.presentValueDiscount ?? period.presentValueDiscountAmount ?? 0),
                            formatCurrency(period.presentValue),
                        ]);
                    });
                    rows.push([
                        `${yearItem.year} Total`, '', '', '',
                        formatCurrency(yearGross),
                        '',
                        formatCurrency(yearNet),
                        formatCurrency(yearItem.annualPresentValueDiscountAmount),
                        formatCurrency(yearItem.annualPresentValue)
                    ]);
                    grandTotalGross += yearGross;
                    grandTotalNet += yearNet;
                    grandTotalPVDiscount += yearItem.annualPresentValueDiscountAmount || 0;
                    grandTotalPV += yearItem.annualPresentValue || 0;
                });
                rows.push([
                    'Total', '', '', '',
                    formatCurrency(grandTotalGross),
                    '',
                    formatCurrency(grandTotalNet),
                    formatCurrency(grandTotalPVDiscount),
                    formatCurrency(grandTotalPV)
                ]);
                return rows;
            })(),
            theme: 'grid',
            styles: { fontSize: 9 },
            columnStyles: {
                2: { halign: 'right' },
                4: { halign: 'right' },
                6: { halign: 'right' },
                7: { halign: 'right' },
                8: { halign: 'right' },
            },
            didParseCell: function (data) {
                if (
                    data.row.raw &&
                    typeof data.row.raw[0] === 'string' &&
                    (data.row.raw[0].endsWith('Total') || data.row.raw[0] === 'Total')
                ) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                }
            }
        });
        y = pdf.lastAutoTable.finalY + 8;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(13);
        pdf.setDrawColor(200);
        y += 6;
        pdf.setFillColor(220, 220, 220);
        pdf.rect(15, y, 180, 12, 'F');
        pdf.setTextColor(0, 0, 0);
        pdf.text(
            `Grand Total (Past Loss + PV of Future Loss): ${formatCurrency(
                (incomeSummary?.totalPastLoss || 0) + (incomeSummary?.totalFuturePresentValue || 0)
            )}`,
            105,
            y + 8,
            { align: 'center' }
        );
        y += 16;
    }

    return y;
};

export default EconomicDamagesSummary;